<script setup>
import {ref, watch} from "vue";
import {useLanguageStore} from "@/pinia";
import {useToast} from "@/composables/useToast";
import axios from "@/axios";

const toast = useToast();
const languageStore = useLanguageStore();

// Form ref
const formRef = ref(null);
const isFormValid = ref(false);
const loading = ref(false);

// Form data
const userId = ref("");
const userName = ref("");
const userEmail = ref("");
const userContact = ref("");
const userPwd = ref("");
const userPwdConfirm = ref("");
const userRole = ref("USER");

// Role options
const roleOptions = [
  { title: languageStore.get('admin'), value: 'ADMIN' },
  { title: languageStore.get('editor'), value: 'EDITOR' },
  { title: languageStore.get('user'), value: 'USER' },
];

// Validation rules
const rules = {
  required: v => !!v || languageStore.get('requiredField'),
  email: v => !v || /.+@.+\..+/.test(v) || languageStore.get('invalidEmail'),
  passwordMatch: v => !v || v === userPwd.value || languageStore.get('passwordMismatch')
};

const props = defineProps({
  isUserEditModal: Boolean,
  userData: Object,
})

const emits = defineEmits([
  'set_isUserEditModal',
  'refresh'
])

// Watch for modal open and populate data
watch(() => props.isUserEditModal, (newVal) => {
  if (newVal && props.userData) {
    populateForm();
  }
});

watch(() => props.userData, (newVal) => {
  if (newVal && props.isUserEditModal) {
    populateForm();
  }
});

const populateForm = () => {
  if (props.userData) {
    userId.value = props.userData.userId || "";
    userName.value = props.userData.name || "";
    userEmail.value = props.userData.email || "";
    userContact.value = props.userData.phone || "";
    userRole.value = props.userData.role || "USER";
    userPwd.value = "";
    userPwdConfirm.value = "";
  }
};

const close = () => {
  userId.value = "";
  userName.value = "";
  userEmail.value = "";
  userContact.value = "";
  userPwd.value = "";
  userPwdConfirm.value = "";
  userRole.value = "USER";
  formRef.value?.reset();
  emits('set_isUserEditModal', false);
}

const save = async () => {
  // v-form validation
  const {valid} = await formRef.value.validate();
  if (!valid) {
    toast.warning(languageStore.get('pleaseCheckInputFields'));
    return;
  }

  // Password confirmation check
  if (userPwd.value && userPwd.value !== userPwdConfirm.value) {
    toast.warning(languageStore.get('passwordMismatch'));
    return;
  }

  loading.value = true;

  const updateData = {
    id: userId.value,
    name: userName.value,
    email: userEmail.value,
    phone: userContact.value,
    role: userRole.value,
  };

  // Only include password if provided
  if (userPwd.value) {
    updateData.password = userPwd.value;
  }

  try {
    await axios.put('/api/users', updateData);
    toast.success(languageStore.get('saved'));
    emits('refresh');
    close();
  } catch (error) {
    console.error('Failed to update user:', error);
    if (error.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error(languageStore.get('errorOccurred'));
    }
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <v-dialog
      :model-value="isUserEditModal"
      @update:model-value="close"
      width="500"
  >
    <v-card>
      <v-card-title class="modal-header">
        {{ languageStore.get('userEdit') }}
      </v-card-title>

      <v-card-text class="modal-body">
        <v-form ref="formRef" v-model="isFormValid">
          <v-text-field
              v-model="userId"
              :label="languageStore.get('userId')"
              variant="outlined"
              density="compact"
              disabled
              bg-color="grey-lighten-3"
          />

          <v-text-field
              v-model="userName"
              :label="languageStore.get('name') + ' *'"
              variant="outlined"
              density="compact"
              :rules="[rules.required]"
          />

          <v-text-field
              v-model="userEmail"
              :label="languageStore.get('email')"
              type="email"
              variant="outlined"
              density="compact"
              :rules="[rules.email]"
          />

          <v-text-field
              v-model="userContact"
              :label="languageStore.get('phoneNumber')"
              variant="outlined"
              density="compact"
          />

          <v-select
              v-model="userRole"
              :label="languageStore.get('role')"
              :items="roleOptions"
              item-title="title"
              item-value="value"
              variant="outlined"
              density="compact"
          />

          <v-divider class="my-4" />

          <div class="text-caption text-grey mb-2">
            {{ languageStore.get('passwordChangeHint') }}
          </div>

          <v-text-field
              v-model="userPwd"
              :label="languageStore.get('newPassword')"
              type="password"
              variant="outlined"
              density="compact"
          />

          <v-text-field
              v-model="userPwdConfirm"
              :label="languageStore.get('confirmPassword')"
              type="password"
              variant="outlined"
              density="compact"
              :rules="[rules.passwordMatch]"
              hide-details="auto"
          />
        </v-form>
      </v-card-text>

      <v-card-actions class="modal-footer">
        <v-spacer/>
        <v-btn
            color="primary"
            variant="flat"
            :loading="loading"
            @click="save"
        >
          {{ languageStore.get('save') }}
        </v-btn>
        <v-btn
            variant="outlined"
            @click="close"
        >
          {{ languageStore.get('cancel') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
/* 모달 헤더 */
.modal-header {
  background-color: #1A3A5C !important;
  color: #FFFFFF !important;
  padding: 16px 24px;
  font-weight: 600;
  font-size: 18px;
}

/* 모달 바디 */
:deep(.v-card-text.modal-body) {
  padding: 24px !important;
}

/* 모달 바디 내 입력 필드 간격 */
.modal-body :deep(.v-input) {
  margin-bottom: 16px;
}

.modal-body :deep(.v-input:last-child) {
  margin-bottom: 0;
}

/* 모달 푸터 */
:deep(.v-card-actions.modal-footer) {
  padding: 16px 24px !important;
  border-top: 1px solid #DEE2E6;
  gap: 8px;
}
</style>
